import { ChangeDetectionStrategy, Component, NgZone } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, distinctUntilChanged, map, Observable } from 'rxjs';
import { Customers } from 'server/shared/collections/customers';
import { Users } from 'server/shared/collections/users';
import { randomId } from 'server/shared/helpers/random';
import { AssignmentDefault } from 'server/shared/models/assignmentDefault';
import { AssignmentTypeTabEnum } from 'server/shared/models/assignmentTypeTab.enum';
import { Certificate } from 'server/shared/models/certificate';
import { Customer } from 'server/shared/models/customer';
import { Department } from 'server/shared/models/department';
import { RoleEnum } from 'server/shared/models/role.enum';
import { Speciality } from 'server/shared/models/speciality';
import { DispoUser } from 'server/shared/models/user';
import { CustomerService } from '../../services/customer.service';
import { OrganisationService } from '../../services/organisation.service';
import { ToastService } from '../../services/toast/toast.service';
@Component({
  selector: 'admin-assignments-defaults',
  templateUrl: 'assignmentDefaults.html',
  styleUrls: ['assignmentDefaults.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignmentDefaultsComponent {
  private _tab: string = '';
  customer$ = new BehaviorSubject<Customer | undefined>(undefined);
  user$ = new BehaviorSubject<DispoUser | undefined>(undefined);
  jobDefaults: AssignmentDefault[] = [];
  employmentDefaults: AssignmentDefault[] = [];
  excludedDepartmentIds$ = new BehaviorSubject<string[]>([]);
  assignmentDefaults$ = new BehaviorSubject<AssignmentDefault[]>([]);

  tabs$ = new BehaviorSubject<
    { label: string; value: AssignmentTypeTabEnum }[]
  >([
    {
      label: 'jobsPool',
      value: AssignmentTypeTabEnum.JOBS,
    },
    {
      label: 'employmentsPool',
      value: AssignmentTypeTabEnum.EMPLOYMENTS,
    },
  ]);

  tabParam$: Observable<AssignmentTypeTabEnum> = this.route.queryParams.pipe(
    map((params) => {
      this.setAssignmentDefaults();
      this._tab = params.tab ?? AssignmentTypeTabEnum.JOBS;
      return params.tab ?? AssignmentTypeTabEnum.JOBS;
    }),
    // distinctUntilChanged is needed in case of other params changing
    distinctUntilChanged(),
  );

  currentUserTracker?: Tracker.Computation;

  constructor(
    private ngZone: NgZone,
    private location: Location,
    private toastService: ToastService,
    private route: ActivatedRoute,
    private router: Router,
    private customerService: CustomerService,
    private organisationService: OrganisationService,
  ) {}

  ngOnInit(): void {
    this.currentUserTracker = Tracker.autorun(() => {
      const user = Users.findOne({
        _id: Meteor.userId(),
        role: RoleEnum.DISPO,
      }) as DispoUser;

      let customer = Customers.findOne({ _id: user?.customerId });
      this.ngZone.run(() => {
        if (
          user &&
          !user.dispoPermissions?.dispoAccess?.featurePermissions
            ?.isCustomerAdmin
        ) {
          this.historyBack();
        }
        this.customer$.next(customer);
        this.user$.next(user);
        this.setAssignmentDefaults();
      });
    });
  }

  ngOnDestroy() {
    this.currentUserTracker?.stop();
  }

  switchTab(tab: string): void {
    this._tab = tab;

    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: { tab: tab },
        replaceUrl: true,
        queryParamsHandling: 'merge',
      })
      .then();
  }

  async selectDepartment(
    assignmentDefault: AssignmentDefault,
    department?: Department,
  ) {
    if (!department) {
      assignmentDefault.department = {
        id: '',
        name: '',
      };
      assignmentDefault.organisation = {
        id: '',
        name: '',
      };
      return;
    }

    await this.organisationService
      .getOrganisationAndDepartmentById(department.id)
      .then((result) => {
        if (!result) {
          return;
        }
        if (result.department) {
          assignmentDefault.department = {
            id: department.id,
            name: result.department.name,
          };
          assignmentDefault.organisation = {
            id: result.organisation._id,
            name: result.organisation.name,
          };
        }
      });
    this.setAlreadyAssignedDepartments();
  }
  setAlreadyAssignedDepartments() {
    let excludedDepartmentIds: string[] = [];
    if (this._tab === AssignmentTypeTabEnum.JOBS) {
      this.jobDefaults?.forEach((defaults) => {
        excludedDepartmentIds.push(defaults.department.id);
      });
    } else {
      this.employmentDefaults?.forEach((defaults) => {
        excludedDepartmentIds.push(defaults.department.id);
      });
    }

    this.excludedDepartmentIds$.next(excludedDepartmentIds);
  }

  setAssignmentDefaults(reset?: boolean): void {
    if (!this._tab) return;
    const customer = this.customer$.value;
    if (!this.jobDefaults.length || reset) {
      this.jobDefaults = [...(customer?.defaultJobValues || [])];
    }
    if (!this.employmentDefaults.length || reset) {
      this.employmentDefaults = [...(customer?.defaultEmploymentValues || [])];
    }
    if (this._tab === AssignmentTypeTabEnum.JOBS) {
      this.assignmentDefaults$.next(this.jobDefaults);
    } else {
      this.assignmentDefaults$.next(this.employmentDefaults);
    }

    this.setAlreadyAssignedDepartments();
  }

  addNewDefault(): void {
    let newDefault: AssignmentDefault = {
      id: randomId(),
      department: {
        id: '',
        name: '',
      },
      organisation: {
        id: '',
        name: '',
      },
      values: {},
    };
    if (this._tab === AssignmentTypeTabEnum.JOBS) {
      this.jobDefaults.push(newDefault);
    } else {
      this.employmentDefaults.push(newDefault);
    }
    this.setAssignmentDefaults();
  }

  removeDefault(departmentId: string): void {
    if (this._tab === AssignmentTypeTabEnum.JOBS) {
      this.jobDefaults = this.jobDefaults.filter(
        (defaults) => defaults.department.id !== departmentId,
      );
    } else {
      this.employmentDefaults = this.employmentDefaults.filter(
        (defaults) => defaults.department.id !== departmentId,
      );
    }
    const filteredDefaults = this.assignmentDefaults$.value.filter(
      (defaults) => defaults.department.id !== departmentId,
    );
    this.assignmentDefaults$.next(filteredDefaults);
  }

  cancelChanges() {
    this.setAssignmentDefaults(true);
  }

  historyBack() {
    this.location.back();
  }

  saveDefaults(): void {
    const customer = this.customer$.value;
    if (!customer) return;
    Meteor.call(
      'updateDefaultAssignments',
      customer._id,
      this.jobDefaults.filter((defaults) => defaults.department.id),
      this.employmentDefaults.filter((defaults) => defaults.department.id),
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
        }
      },
    );
  }

  certificatesSelected(
    assignmentDefault: AssignmentDefault,
    certificates: Certificate[],
  ) {
    assignmentDefault.values.certificates = certificates.map((certificate) => {
      return {
        id: certificate._id,
        displayName: certificate.name,
      };
    });
  }

  specialitiesSelected(
    assignmentDefault: AssignmentDefault,
    specialities: Speciality[],
  ) {
    assignmentDefault.values.specialities = specialities.map((speciality) => {
      return {
        id: speciality._id,
        displayName: speciality.name,
      };
    });
  }

  poolerTypesSelected(
    assignmentDefault: AssignmentDefault,
    poolerTypeIds: string[],
  ) {
    const customer = this.customer$.value;
    const customerPoolerTypes = customer?.poolerTypes || [];
    assignmentDefault.values.poolerTypes = customerPoolerTypes.filter(
      (poolerType) => poolerTypeIds.includes(poolerType.id),
    );
  }
}
